import { parseISO } from "date-fns"
import {
  type TimelineChain,
  type TimelineChainEvent,
} from "@/components/timeline/buildTimelineData"

const DAY_IN_MS = 24 * 60 * 60 * 1000

// A transport nobody has closed never reads as finished, however far past its
// plan it has drifted — a full bar is reserved for "every stop served".
const MAX_UNFINISHED_PROGRESS = 0.95

const toTime = (value?: string): number | null => {
  if (!value) {
    return null
  }
  const parsed = parseISO(value).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

// Same preference order lateness already uses: the tacho-aware ETA is the one
// that knows the driver has to stop, so it wins where both exist. Scheduled
// time is the last resort — a stop nothing is tracking still has a plan.
const arrivalTime = (event: TimelineChainEvent): number | null =>
  toTime(event.estimatedArrivalTimeWithTacho) ??
  toTime(event.estimatedArrivalTime) ??
  toTime(event.scheduledTime)

const isServed = (event: TimelineChainEvent): boolean =>
  toTime(event.realTime) !== null

/**
 * How much of a transport is behind the driver, as 0..1 of the bar's own
 * width, or null when we can't honestly say.
 *
 * The bar's axis is planned time — buildChainDrafts places it between its
 * first and last stop's schedule — so the fill has to speak that axis too. It
 * doesn't measure elapsed time against an estimate (that says nothing about
 * lateness, because the estimate moves with the delay); it projects the live
 * ETA of the next unserved stop back onto the plan: a stop planned for 18:00
 * whose ETA is two hours out at 12:00 puts the driver at the plan's 16:00, so
 * the fill stops there.
 *
 * That keeps the documented reading exact: a driver running to plan lands the
 * fill edge on the now-line, behind plan leaves it short of the line, ahead of
 * plan pushes it past — including for a transport loaded before its slot,
 * whose bar sits in the future while the truck is already on it.
 *
 * Stops served earlier act as a floor: a hopeless ETA on the last stop can't
 * un-drive the ones the truck demonstrably passed.
 */
export const chainProgress = (
  chain: TimelineChain,
  now: Date,
): number | null => {
  const served = chain.events.filter(isServed)
  // Nothing served yet means nothing to vouch for — including feeds (G-Box,
  // Platform Science) that report completion as a flag with no timestamp.
  if (served.length === 0) {
    return null
  }
  if (served.length === chain.events.length) {
    return 1
  }

  const span = chain.endOffset - chain.startOffset
  if (span <= 0) {
    return null
  }

  // buildChainDrafts sorts a chain's events by time, so the first unserved one
  // is the stop the driver is working towards.
  const nextStop = chain.events.find((event) => !isServed(event))
  const arrivesAt = nextStop ? arrivalTime(nextStop) : null
  if (nextStop === undefined || arrivesAt === null) {
    return null
  }

  // Clamped at zero: an ETA already in the past means the driver is at the
  // stop, not past it — that stop's own planned slot is as far as we can claim.
  const remainingDays = Math.max((arrivesAt - now.getTime()) / DAY_IN_MS, 0)
  const lastServedOffset = Math.max(...served.map((event) => event.offset))
  const edgeOffset = Math.max(nextStop.offset - remainingDays, lastServedOffset)

  const progress = Math.min(
    Math.max((edgeOffset - chain.startOffset) / span, 0),
    MAX_UNFINISHED_PROGRESS,
  )
  return progress
}
